<template>
  <LISAUIButton
    v-if="isLISA3"
    v-bind="{ ...$attrs, ...$props, icon: undefined }"
    :size="size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'md'"
    :data-testid="dataTestid"
    :variant="variant"
    :outlined="outlined"
    :icon="$props.icon"
    :icon-placement="iconPlacement"
  >
    <template v-if="'icon' in $slots" #icon>
      <slot name="icon" />
    </template>
    <template v-if="$props.label != null || 'default' in $slots || hasSubtitle" #default>
      <slot>{{ $props.label }}</slot>
    </template>
  </LISAUIButton>
  <PrimeVueButton v-else v-bind="{ ...$attrs, ...forwardedProps }" :data-testid="dataTestid">
    <div v-if="'default' in $slots" class="text-truncate w-100">
      <span v-if="hasSubtitle" class="lisa-app-button__text">
        <span class="lisa-app-button__label"><slot /></span>
        <span class="lisa-app-button__subtitle">
          <slot name="subtitle">{{ $props.subtitle }}</slot>
        </span>
      </span>
      <span v-else><slot /></span>
    </div>
    <template v-if="'icon' in $slots">
      <slot name="icon"></slot>
    </template>
  </PrimeVueButton>
</template>

<script setup lang="ts">
/**
 * ! Vue does not support Generic Types in Props and Emits
 * ? We need to manually specify the types currently.... see issue https://github.com/vuejs/core/issues/8482
 */

// eslint-disable-next-line no-restricted-imports
import PrimeVueButton, { type ButtonPassThroughOptions } from "primevue/button";
import { LISAButton as LISAUIButton, type LISAVariant } from "@fi-solutions/lisa-ui";
import { computed, useSlots } from "vue";
import { isLISA3 } from "@/composables/useLISA3";

/* @vue-ignore */
export interface ButtonProps {
  /**
   * Inline style of the button.
   */
  style?: any;
  /**
   * Style class of the button.
   */
  class?: any;
  /**
   * Text of the button.
   */
  label?: string | undefined;
  /**
   * Name of the icon.
   */
  icon?: string | undefined;
  /**
   * Position of the icon.
   * @defaultValue left
   */
  iconPos?: "left" | "right" | "top" | "bottom" | undefined;
  /**
   * Style class of the icon.
   */
  iconClass?: string | undefined;
  /** Secondary text rendered beneath the main label. */
  subtitle?: string | undefined;
  /**
   * Value of the badge.
   */
  badge?: string | undefined;
  /**
   * Style class of the badge.
   */
  badgeClass?: string | undefined;
  /**
   * Whether the button is in loading state.
   * @defaultValue false
   */
  loading?: boolean | undefined;
  /**
   * Icon to display in loading state.
   */
  loadingIcon?: string | undefined;
  /**
   *  Add a link style to the button.
   * @defaultValue false
   */
  link?: boolean | undefined;
  /**
   * Defines the style of the button.
   */
  severity?: "secondary" | "success" | "info" | "warning" | "help" | "danger" | string | undefined;
  /**
   * Add a shadow to indicate elevation.
   * @defaultValue false
   */
  raised?: boolean | undefined;
  /**
   * Add a circular border radius to the button.
   * @defaultValue false
   */
  rounded?: boolean | undefined;
  /**
   * Add a textual class to the button without a background initially.
   * @defaultValue false
   */
  text?: boolean | undefined;
  /**
   * Add a border class without a background initially.
   * @defaultValue false
   */
  outlined?: boolean | undefined;
  /**
   * Defines the size of the button.
   */
  size?: "small" | "large" | undefined;
  /**
   * Add a plain textual class to the button without a background initially.
   * @defaultValue false
   */
  plain?: boolean | undefined;
  /**
   * Uses to pass attributes to DOM elements inside the component.
   * @type {ButtonPassThroughOptions}
   */
  pt?: ButtonPassThroughOptions;

  /**
   * Custom Props
   */
  collapse?: boolean | undefined;

  /**
   * Test ID for testing purposes
   * @requires testId
   */
  dataTestid?: string;
}

const props = defineProps<ButtonProps>();
const slots = useSlots();
const hasSubtitle = computed(() => props.subtitle != null || slots.subtitle != null);
const iconPlacement = computed(() => (props.iconPos === "right" ? "right" : "left"));
const forwardedProps = computed(() => {
  const { subtitle: _subtitle, ...buttonProps } = props;
  return buttonProps;
});

const variant = computed<LISAVariant>(() => {
  switch (props.severity as "secondary" | "success" | "info" | "warning" | "help" | "danger") {
    case "danger":
      return "error";
    case "help":
    case "info":
      return "info";
    case "secondary":
      return "secondary";
    case "success":
      return "success";
    case "warning":
      return "warning";
    default:
      return "primary";
  }
});
</script>

<style lang="scss" scoped>
.lisa-app-button__text {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.lisa-app-button__label,
.lisa-app-button__subtitle {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lisa-app-button__label {
  line-height: 1.1;
}

.lisa-app-button__subtitle {
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1.15;
  opacity: 0.8;
}
</style>
